import { useContext, useMemo } from "react";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import "./pointsOutcomeView.scss";

const formatSlug = (slug) =>
  slug
    ? slug
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : "";

const AwardPurchaseBasedPointsView = ({ data, definition, customComponentName }) => {
  // Points data lives in outcomeRules[].ruleData.points_calculation (not in outcomeData).
  // Also handle legacy formats: data.points_calculation or double-nested.
  const pointsCalc = useMemo(() => {
    // Primary: extract from outcomeRules ruleData
    const rules = definition?.outcomeRules || [];
    for (const rule of rules) {
      const pc = rule?.ruleData?.points_calculation;
      if (pc && pc.rule_type) return pc;
    }
    // Fallback: try outcomeData.points_calculation (legacy)
    const pc = data?.points_calculation || {};
    return pc.points_calculation || pc;
  }, [data, definition?.outcomeRules]);
  const { programConfigurations } = useContext(GlobalConfigContext);

  const tierMultipliersEnabled = pointsCalc.tier_multipliers_enabled || false;

  const tierOverrides = useMemo(
    () => pointsCalc.tier_multiplier_overrides || [],
    [pointsCalc]
  );

  const productMultipliers = useMemo(
    () => pointsCalc.product_multipliers || [],
    [pointsCalc]
  );

  const boosterMultipliers = useMemo(
    () => pointsCalc.booster_multipliers || [],
    [pointsCalc]
  );

  const boosterFlatPoints = useMemo(
    () => pointsCalc.booster_flat_points || [],
    [pointsCalc]
  );

  const pointsCapAlert = useMemo(
    () => pointsCalc.points_cap_alert || {},
    [pointsCalc]
  );

  const fraudAlertRecipients = useMemo(
    () => pointsCalc.fraud_alert_recipients || "",
    [pointsCalc]
  );

  const baseEarnRate = useMemo(() => {
    const config = programConfigurations?.find(
      (c) => c.key === "global_points_configuration"
    );
    const configData = config?.value?.data;
    if (configData?.base_earn_rate) return configData.base_earn_rate;
    if (configData?.joy_points?.points) return configData.joy_points.points;
    return null;
  }, [programConfigurations]);

  const timeBoundTierCount = useMemo(
    () => tierOverrides.filter((m) => m.effective_start_date).length,
    [tierOverrides]
  );

  const defaultProductRulesCount = useMemo(
    () => productMultipliers.filter((m) => m.type === "default").length,
    [productMultipliers]
  );

  const timeBoundProductCount = useMemo(
    () => productMultipliers.filter((m) => m.type === "time_bounded").length,
    [productMultipliers]
  );

  const totalBoostersCount = useMemo(
    () => boosterMultipliers.length + boosterFlatPoints.length,
    [boosterMultipliers, boosterFlatPoints]
  );

  return (
    <div
      className="points-outcome-view"
      data-testid="award-purchase-based-points-view"
    >
      <h5>
        {definition?.outcomeDefinition?.name || formatSlug(customComponentName)}
      </h5>
      <ul className="outcome-summary-list">
        <li>
          Basic Configuration:{" "}
          <strong>
            Base Earn Rate -{" "}
            {baseEarnRate
              ? `${baseEarnRate} point${baseEarnRate > 1 ? "s" : ""} for every $1 spend`
              : "Not configured"}
          </strong>
        </li>
        <li>
          Tiers Multipliers:{" "}
          <strong>{tierMultipliersEnabled ? "Enable" : "Disable"}</strong>
          {tierMultipliersEnabled && (
            <ul className="outcome-summary-sub-list">
              <li>
                Time Bounded Rules: <strong>{timeBoundTierCount}</strong>
              </li>
            </ul>
          )}
        </li>
        <li>
          Product and Group Rule Configuration:{" "}
          <strong>{defaultProductRulesCount} Default Rules</strong>
          <ul className="outcome-summary-sub-list">
            <li>
              Time Bounded Rules: <strong>{timeBoundProductCount}</strong>
            </li>
          </ul>
        </li>
        <li>
          Time-Bound Boosters: <strong>{totalBoostersCount}</strong>
        </li>
        <li>
          Total Order or Receipt Points Cap Alert:{" "}
          <strong>
            {pointsCapAlert.enabled
              ? `Enable - ${pointsCapAlert.points_cap} points`
              : "Disable"}
          </strong>
          {pointsCapAlert.enabled && fraudAlertRecipients && (
            <ul className="outcome-summary-sub-list">
              <li>
                Fraud Alert Recipients: <strong>{fraudAlertRecipients}</strong>
              </li>
            </ul>
          )}
        </li>
      </ul>
    </div>
  );
};

export default AwardPurchaseBasedPointsView;
